import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Flag, CheckCircle2, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { DataTable } from "@/components/common/DataTable";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RowActions } from "./RowActions";
import { Button } from "@/components/ui/button";
import { useCrud } from "@/hooks/useCrud";
import { useUser } from "@/context/UserContext";
import {
  OWNERS,
  PRIORITY_LEVELS,
  PRIORITY_STATUS,
  priorityBadge,
  statusBadge,
} from "@/data/commandOptions";

const RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const today = () => new Date().toISOString().slice(0, 10);

const FIELDS = [
  { name: "title", label: "Title", type: "text", placeholder: "e.g. Finalise onboarding flow", required: true },
  { name: "description", label: "Description", type: "textarea", placeholder: "What needs to happen?" },
  { name: "owner", label: "Owner", type: "select", placeholder: "Assign owner", options: OWNERS, required: true },
  { name: "priority", label: "Priority level", type: "select", placeholder: "Set priority", options: PRIORITY_LEVELS, required: true },
  { name: "due_date", label: "Due date", type: "date" },
  { name: "status", label: "Status", type: "select", placeholder: "Set status", options: PRIORITY_STATUS, required: true },
  { name: "latest_update", label: "Latest update", type: "textarea", placeholder: "Most recent note" },
];

export const PrioritiesPanel = ({ filter }) => {
  const { items, save, remove, patch } = useCrud("priorities", "Priority");
  const { canWrite } = useUser();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const openAdd = () => { setEditing({ owner: "Steven", priority: "Medium", status: "Not started" }); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const overdueOnly = filter === "overdue";
  let rows = items.filter((p) => (showCompleted ? p.status === "Complete" : p.status !== "Complete"));
  if (overdueOnly) rows = rows.filter((p) => p.due_date && p.due_date < today() && p.status !== "Complete");

  const columns = [
    { key: "title", header: "Priority", render: (r) => (
      <div>
        <p className="font-medium text-[#0D1B2A]">{r.title}</p>
        {r.due_date && <p className="mt-0.5 text-xs text-[#5C6B7B]">Due {r.due_date}</p>}
      </div>
    )},
    { key: "owner", header: "Owner" },
    { key: "priority", header: "Priority", render: (r) => <StatusBadge status={priorityBadge(r.priority)} label={r.priority} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={statusBadge(r.status)} label={r.status} /> },
    { key: "actions", header: "", thClassName: "text-right", className: "text-right", render: (r) => (
      <RowActions
        idx={r.id}
        onEdit={() => openEdit(r)}
        onDelete={() => setConfirmId(r.id)}
        extra={r.status !== "Complete" && (
          <Button size="sm" variant="ghost" data-testid={`complete-${r.id}`}
            onClick={() => patch(r.id, { status: "Complete" }, "Priority marked complete")}
            className="h-8 gap-1 text-[#2D6A4F] hover:bg-[#2D6A4F]/10">
            <CheckCircle2 size={15} /> Complete
          </Button>
        )}
      />
    )},
  ];

  return (
    <SectionCard
      testid="priorities-card"
      accent="#06B6D4"
      eyebrow="Active Missions"
      icon={Flag}
      title="Weekly Priorities"
      subtitle={showCompleted ? "Completed & archived missions." : "Active missions steering the expedition this week."}
      action={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="toggle-completed-priorities"
            onClick={() => setShowCompleted((s) => !s)} className="rounded-full border-[#E4E9F3]">
            {showCompleted ? "View Active" : "View Completed"}
          </Button>
          {canWrite && <Button size="sm" data-testid="add-priority-btn" onClick={openAdd} className="rounded-full bg-[#06B6D4] text-white hover:bg-[#0891B2]">
            <Plus size={15} /> Add
          </Button>}
        </div>
      }
    >
      {overdueOnly && (
        <div data-testid="priorities-filter-banner" className="mb-3 flex items-center justify-between rounded-xl bg-[#F97316]/10 px-3 py-2 text-xs font-semibold text-[#C2410C]">
          <span>Filtered to overdue priorities</span>
          <button data-testid="clear-filter" onClick={() => navigate("/?tab=priorities")} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear</button>
        </div>
      )}
      {!showCompleted && rows.length > 5 && (
        <p className="mb-3 rounded-xl bg-[#06B6D4]/10 px-3 py-2 text-xs font-medium text-[#0E7490]">
          You have {rows.length} active priorities — keeping to five keeps the week focused.
        </p>
      )}
      <DataTable
        testid="priorities-table"
        columns={columns}
        rows={rows}
        searchKeys={["title", "owner", "description"]}
        searchPlaceholder="Search priorities…"
        filters={[
          { key: "owner", label: "Owner", options: OWNERS },
          { key: "priority", label: "Priority", options: PRIORITY_LEVELS },
          { key: "status", label: "Status", options: PRIORITY_STATUS },
        ]}
        sorts={[
          { value: "due", label: "Due date", fn: (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999") },
          { value: "prio", label: "Priority", fn: (a, b) => (RANK[b.priority] || 0) - (RANK[a.priority] || 0) },
        ]}
        emptyIcon={Flag}
        emptyImage={showCompleted || overdueOnly ? undefined : "/commando-mascot-v2.webp"}
        emptyTitle={overdueOnly ? "No overdue priorities" : showCompleted ? "No completed priorities" : "No active priorities"}
        emptyDescription={overdueOnly ? "Nothing is past its due date — nicely on track." : showCompleted ? "Completed priorities will be archived here." : "Add the first weekly priority to focus the team."}
        emptyActionLabel={showCompleted || overdueOnly || !canWrite ? undefined : "Add priority"}
        onEmptyAction={showCompleted || overdueOnly || !canWrite ? undefined : openAdd}
      />

      <EntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing?.id ? "Edit Priority" : "Add Weekly Priority"}
        submitLabel={editing?.id ? "Save Changes" : "Add Priority"}
        fields={FIELDS}
        initialValues={editing}
        onSubmit={save}
      />
      <ConfirmDialog
        open={!!confirmId}
        onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete this priority?"
        description="This weekly priority will be permanently removed."
        onConfirm={() => { remove(confirmId); setConfirmId(null); }}
      />
    </SectionCard>
  );
};
